import api from './api';

// --- Interfaces ---

export interface Lead {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  jobTitle?: string;
  linkedInUrl?: string;
  phone?: string;
  website?: string;
  location?: string;
  clayEnrichedAt?: string;
  clayData?: string;
  icebreakerEmail?: string;
  icebreakerSubject?: string;
  icebreakerLinkedin?: string;
  icebreakerVariants?: string; // JSON: { email: Variant[], linkedin: Variant[] }
  icebreakerGeneratedAt?: string;
  lemlistCampaignId?: string;
  lemlistLeadId?: string;
  lemlistSyncedAt?: string;
  lemlistStatus?: string;
  source?: string;
  tags?: string;
  notes?: string;
  status: string;
  /** Firmenakte, sobald der Lead einer Firma zugeordnet ist ("Leads zuordnen" unter Firmen). */
  companyId?: string | null;
  userId: string;
  tenantId?: string;
  createdAt: string;
  updatedAt: string;
  /** Nur in der Liste: Vorgaenge (Pipeline) und ICP-Kampagnen dieses Leads. */
  deals?: { id: string; title: string; status: string }[];
  icpCampaignProspects?: { campaign: { id: string; name: string } }[];
}

export type KampagnenArt = 'icp' | 'signal' | 'sequenz' | 'mailing';

/** Eigene Kampagne hinter einer Lemlist-Kampagne. */
export interface KampagnenZuordnung {
  art: KampagnenArt;
  id: string;
  name: string;
  lemlistCampaignId: string;
}

export interface CreateLeadInput {
  email: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  jobTitle?: string;
  linkedInUrl?: string;
  phone?: string;
  website?: string;
  location?: string;
  source?: string;
  tags?: string[];
  notes?: string;
}

export interface UpdateLeadInput {
  email?: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  jobTitle?: string;
  linkedInUrl?: string;
  phone?: string;
  website?: string;
  location?: string;
  source?: string;
  tags?: string[];
  notes?: string;
  // Manuell überarbeitbare Icebreaker (Review vor dem Sync).
  icebreakerEmail?: string;
  icebreakerSubject?: string;
  icebreakerLinkedin?: string;
}

export interface BulkImportResult {
  created: number;
  skipped: number;
  errors: Array<{ email: string; error: string }>;
}

export interface EnrichResult {
  enriched: number;
  failed: number;
  details: Array<{ email: string; success: boolean; error?: string }>;
}

export interface SyncToLemlistResult {
  synced: number;
  failed: number;
  details: Array<{ email: string; success: boolean; lemlistLeadId?: string; error?: string }>;
}

export interface LemlistSyncPreviewLead {
  id: string;
  email: string;
  name?: string;
  company?: string;
  alreadySynced: boolean;
  customFields: Record<string, string>;
  warnings: string[];
  blocking: boolean;
  renderedSubject?: string;
  renderedBody?: string;
}

export interface LemlistMessageTemplate {
  subject?: string;
  body?: string;
}

export interface LemlistSyncPreview {
  leads: LemlistSyncPreviewLead[];
  summary: {
    total: number;
    syncable: number;
    blocked: number;
    missingEmailIcebreaker: number;
    missingLinkedinIcebreaker: number;
  };
}

export interface EnrichAndSyncResult {
  enrichResult: EnrichResult;
  syncResult: SyncToLemlistResult;
}

export interface GenerateIcebreakersInput {
  channels?: ('email' | 'linkedin')[];
  autoSources?: ('research' | 'website' | 'signal')[];
  tone?: string;
  language?: string;
  goal?: string;
  icpId?: string;
  personaId?: string;
  offerId?: string;
  variantCount?: number; // 1–3 Varianten je Kanal (A/B-Test)
}

export interface GenerateIcebreakersResult {
  generated: number;
  failed: number;
  details: Array<{ email: string; success: boolean; error?: string }>;
}

export interface LemlistCampaign {
  _id: string;
  name: string;
  status?: string;
  labels?: string[];
}

export interface LeadFilters {
  status?: string;
  source?: string;
  tag?: string;
}

// --- Service ---

class LeadSyncService {
  // Lead CRUD
  async getLeads(filters?: LeadFilters): Promise<Lead[]> {
    const response = await api.get('/leads', { params: filters || {} });
    return response.data;
  }

  async getLead(id: string): Promise<Lead> {
    const response = await api.get(`/leads/${id}`);
    return response.data;
  }

  async createLead(input: CreateLeadInput): Promise<Lead> {
    const response = await api.post('/leads', input);
    return response.data;
  }

  async updateLead(id: string, input: UpdateLeadInput): Promise<Lead> {
    const response = await api.put(`/leads/${id}`, input);
    return response.data;
  }

  async deleteLead(id: string): Promise<void> {
    await api.delete(`/leads/${id}`);
  }

  async bulkImportLeads(leads: CreateLeadInput[]): Promise<BulkImportResult> {
    const response = await api.post('/leads/bulk-import', { leads });
    return response.data;
  }

  // Clay enrichment
  async enrichLeads(leadIds: string[]): Promise<EnrichResult> {
    const response = await api.post('/leads/clay/enrich', { leadIds });
    return response.data;
  }

  async pushToClayTable(leadIds: string[], tableId: string): Promise<any> {
    const response = await api.post('/leads/clay/push', { leadIds, tableId });
    return response.data;
  }

  async pullFromClayTable(tableId: string): Promise<EnrichResult> {
    const response = await api.post('/leads/clay/pull', { tableId });
    return response.data;
  }

  // Icebreaker generation (Clay replacement)
  async generateIcebreakers(
    leadIds: string[],
    options?: GenerateIcebreakersInput
  ): Promise<GenerateIcebreakersResult> {
    const response = await api.post('/leads/icebreakers/generate', { leadIds, ...(options || {}) });
    return response.data;
  }

  // Lemlist sync
  async getKampagnenZuordnung(): Promise<KampagnenZuordnung[]> {
    const response = await api.get('/leads/kampagnen-zuordnung');
    return response.data;
  }

  async getLemlistCampaigns(): Promise<LemlistCampaign[]> {
    const response = await api.get('/lemlist/campaigns');
    return response.data;
  }

  async previewLemlistSync(
    leadIds: string[],
    customFields?: Record<string, string>,
    template?: LemlistMessageTemplate
  ): Promise<LemlistSyncPreview> {
    const response = await api.post('/leads/lemlist/preview', { leadIds, customFields, template });
    return response.data;
  }

  async syncToLemlist(
    leadIds: string[],
    campaignId: string,
    customFields?: Record<string, string>
  ): Promise<SyncToLemlistResult> {
    const response = await api.post('/leads/lemlist/sync', { leadIds, campaignId, customFields });
    return response.data;
  }

  async removeFromLemlist(id: string): Promise<void> {
    await api.delete(`/leads/lemlist/${id}`);
  }

  // Full pipeline
  async enrichAndSync(
    leadIds: string[],
    campaignId: string,
    clayTableId?: string
  ): Promise<EnrichAndSyncResult> {
    const response = await api.post('/leads/pipeline/enrich-and-sync', {
      leadIds,
      campaignId,
      clayTableId,
    });
    return response.data;
  }
}

const leadSyncService = new LeadSyncService();
export default leadSyncService;
